import { Check, X, Star, Trash2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReviewManager = ({ reviews, token, credentials, onRefresh }) => {
  // Get auth config (JWT or Basic Auth fallback)
  const getAuthConfig = () => {
    if (token) {
      return { headers: { Authorization: `Bearer ${token}` } };
    }
    if (credentials?.username && credentials?.password) {
      return { auth: { username: credentials.username, password: credentials.password } };
    }
    return {};
  };

  const handleApprove = async (id) => {
    try {
      const config = getAuthConfig();
      await axios.put(`${API}/reviews/${id}/approve`, {}, config);
      toast.success("Review approved!");
      onRefresh();
    } catch {
      toast.error("Failed to approve review");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this review?")) return;

    try {
      const config = getAuthConfig();
      await axios.delete(`${API}/reviews/${id}`, config);
      toast.success("Review deleted!");
      onRefresh();
    } catch {
      toast.error("Failed to delete review");
    }
  };

  return (
    <>
      <h2 className="text-xl font-bold text-[#0A0A0A] mb-6">
        Reviews ({reviews.length})
      </h2>
      <div className="space-y-4">
        {reviews.map((review) => (
          <div
            key={review.id}
            className={`bg-white border p-4 ${
              review.approved ? "border-green-200" : "border-yellow-200"
            }`}
            data-testid={`review-card-${review.id}`}
          >
            <div className="flex justify-between items-start">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="font-bold text-[#0A0A0A]">
                    {review.customer_name}
                  </span>
                  <span className="text-sm text-[#525252]">
                    - {review.location}
                  </span>
                  <span
                    className={`text-xs px-2 py-1 ${
                      review.approved
                        ? "bg-green-100 text-green-700"
                        : "bg-yellow-100 text-yellow-700"
                    }`}
                  >
                    {review.approved ? "Approved" : "Pending"}
                  </span>
                </div>
                <div className="flex mb-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={star}
                      className={`w-4 h-4 ${
                        star <= review.rating
                          ? "text-[#FF6B00] fill-[#FF6B00]"
                          : "text-gray-300"
                      }`}
                    />
                  ))}
                </div>
                <p className="text-[#525252] text-sm">{review.review_text}</p>
                {review.service_type && (
                  <p className="text-xs text-[#525252] mt-2">
                    Service: {review.service_type}
                  </p>
                )}
                {review.image_url && (
                  <div className="mt-3">
                    <img
                      src={review.image_url}
                      alt="Review"
                      className="w-32 h-24 object-cover border border-[#E5E5E5]"
                    />
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                {!review.approved && (
                  <button
                    onClick={() => handleApprove(review.id)}
                    className="p-2 text-green-600 hover:bg-green-50 transition-colors"
                    title="Approve"
                    data-testid={`approve-review-${review.id}`}
                  >
                    <Check className="w-5 h-5" />
                  </button>
                )}
                <button
                  onClick={() => handleDelete(review.id)}
                  className="p-2 text-red-500 hover:bg-red-50 transition-colors"
                  title="Delete"
                  data-testid={`delete-review-${review.id}`}
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        ))}
        {reviews.length === 0 && (
          <p className="text-center text-[#525252] py-8">No reviews yet</p>
        )}
      </div>
    </>
  );
};

export default ReviewManager;
